/**
 * This module shows or hides the Product Menu component as the user scrolls up or down.
 *
 * @module app/components/ProductMenu
 */
import jQuery from 'jquery';
import AnimationFrame from 'animation-frame';

import { isHomePage } from '../../utils';
import deviceDetector from './DeviceDetector';

/**
 * RequestAnimationFrame instance
 *
 * @type    {Object}
 */
var raf = new AnimationFrame();

/**
 * Flag to check if window is scrolling
 *
 * @type {Boolean}
 *
 * @private
 */
var ticking = false;

/**
 * A constant to determine when to trigger animation
 *
 * @type {Integer}
 *
 * @private
 */
var scrollDelta = 2;

/**
 * Keeps last position of y axis
 *
 * @type {Integer}
 *
 * @private
 */
var lastScrollY = 0;

/**
 * Stores the height of the header
 *
 * @type {Integer}
 *
 * @private
 */
var pMenuHeight = 0;

/**
 * Contains the list of DOM elements to be used and their selectors
 *
 * @type {Object}
 *
 * @private
 */
var DOMElements = {

    productMenu: {
        element: null,
        selector: '#p-menu',
    },

    productMenuItem: {
        element: null,
        selector: '.p-menu__item',
    },

    accountPanel: {
        element: null,
        selector: '#account-section > .account',
    },

    mainCanvas: {
        element: null,
        selector: '.off-canvas__wrapper',
    },
    header: {
        element: null,
        selector: '#header',
    },
};

/**
 * Topics for MessageBus
 *
 * @type {Object}
 *
 * @private
 */
var messageBusTopics = {

    domReady: 'AppJs.domReady',
    navigation: 'navigation.pageChanged',
    betslipOpen: 'betslip.openingBetslip',
    betslipClose: 'betslip.closeDrawers',
    myAccountOpen: 'myAccount.open',
    myAccountClose: 'myAccount.close',
};

/**
 * A helper method to get the element and caching its value in the DOMElements object structure
 *
 * @returns {object}
 *
 * @private
 */
var getElement = function getElement(object) {

    if (object.element === null) {

        object.element = jQuery(object.selector);
    }

    return object.element;
};

/**
 * A helper method to check if a given element is into the viewport
 *
 * @param   {Object} elem DOM element
 *
 * @returns {boolean}
 *
 * @private
 */
var isElementInViewport = function (elem) {

    var rect = elem[0].getBoundingClientRect();

    return rect.right <= (window.innerWidth || jQuery(window).width());
};

/**
 * Calculate items width by device viewport
 *
 * @type {Integer}
 *
 * @private
 */
var calculateWidths = function calculateWidths() {

    var viewportWidth = jQuery(window).width() || 0;
    var margin = 28;
    var lastEl = {};
    var halfLastItem,
        total,
        visibleItemsWidth,
        visibleItems = 0;

    var pmi = getElement(DOMElements.productMenuItem);

    jQuery.each(pmi, function () {
        var p = jQuery(this)[0].getBoundingClientRect();

        if (p.left < 0 - jQuery(this).width() || p.left < viewportWidth) {
            lastEl = jQuery(this);
            visibleItems += lastEl.length;
            visibleItemsWidth = lastEl.outerWidth(true);
        }
    });

    if (isElementInViewport(lastEl)) {
        var rect = lastEl[0].getBoundingClientRect();

        halfLastItem = ((viewportWidth - rect.right) + rect.width) / 2;
        total = ((halfLastItem * visibleItems) / margin) + margin;

        pmi.css({ 'margin-right': total + 'px' });
    }
};

/**
 * Fires onScroll event for the product menu
 */
var productMenuScrollStart = function () {

    productMenuScrollOff();
    jQuery(window).on('scroll.productmenu.scroll', onScroll);
};

/**
 * onScroll event for the product menu off
 */
var productMenuScrollOff = function () {

    jQuery(window).off('scroll.productmenu.scroll');
};

/**
 * Fires when window is scrolled. Uses requestAnimationFrame and a flag to debounce the scroll animation.
 * Performance improvement recommended by http://www.html5rocks.com/en/tutorials/speed/animations/
 *
 * @returns {void}
 *
 * @private
 */
var onScroll = function onScroll() {

    if (!ticking&& !getElement(DOMElements.header).hasClass('is-side-active')) {
        ticking = true;
        raf.request(scrollHandler);
    }


};

/**
 * The handler which determines if to update the product menu
 *
 * @returns {void}
 *
 * @private
 */
var scrollHandler = function scrollHandler() {

    var scrollY = window.scrollY;

    if (scrollY < 10) {

        openIt();

    } else {

        closeIt();

    }

    if (Math.abs(lastScrollY - scrollY) >= scrollDelta) {

        updateProductMenuPosition(scrollY);
    }

    ticking = false;
};

/**
 * Fires onScroll event for the product menu
 */
var closeIt = function () {

    getElement(DOMElements.productMenu).removeClass('is-active');
    getElement(DOMElements.mainCanvas).removeClass('product-menu-active');

};

/**
 * Show the product menu
 *
 * @type {Function}
 *
 * @returns {bool}
 *
 * @private
 */
var openIt = function () {

    if (canBeOpen()) {

        getElement(DOMElements.productMenu)
            .addClass('is-active')
            .removeClass('is-not-active product-menu-sports');
        getElement(DOMElements.mainCanvas).addClass('product-menu-active');
        getElement(DOMElements.accountPanel).removeClass('-expanded');

    } else {

        return false;
    }
};

/* Conditions to allow the menu to be open*/
var canBeOpen = function canBeOpen() {

    return !getElement(DOMElements.accountPanel).hasClass('-expanded');
};

/**
 * Check is visible
 *
 * @type {Function}
 *
 * @returns {void}
 *
 * @private
 */
var onNavigation = function onNavigation() {
    var element = getElement(DOMElements.productMenu);

    if (isHomePage()) {
        element.removeClass('is-not-active');
        openIt();
    } else {
        element.addClass('is-not-active product-menu-sports');
        closeIt();
    }
};

/**
 * Set the correct position when the user scrolls up or down
 *
 * @param   {Integer} currentScrollY the current position on the y axis
 *
 * @returns {void}
 *
 * @private
 */
var updateProductMenuPosition = function update(currentScrollY) {

    if (currentScrollY > pMenuHeight) {

        closeIt();
    } else {

        openIt();
    }

    lastScrollY = currentScrollY;
};

/**
 * This will make this module start listening events declared for the DOM in Mobiles and Tables
 *
 * @returns {void}
 *
 * @private
 */
var subscribeToMessageBus = function subscribeToMessageBus() {

    if (!deviceDetector.isDesktop()) {
        window.WH.messageBus.subscribe(messageBusTopics.domReady, onDOMReady);
        window.WH.messageBus.subscribe(messageBusTopics.navigation, onNavigation);

        window.WH.messageBus.subscribe(messageBusTopics.betslipOpen, closeIt);
        window.WH.messageBus.subscribe(messageBusTopics.betslipClose, openIt);

        window.WH.messageBus.subscribe(messageBusTopics.myAccountOpen, closeIt);
        window.WH.messageBus.subscribe(messageBusTopics.myAccountClose, openIt);
    }
};

/**
 * Event handler for DOM Ready
 *
 * @returns {void}
 *
 * @private
 */
var onDOMReady = function onDOMReady() {

    calculateWidths();
    productMenuScrollStart();
    updateProductMenuPosition(window.scrollY);
};

/**
 * Public functions.
 *
 * @type {Object}
 *
 * @public
 */
var api = {

    /**
     * Initializes ProductHeader component
     *
     * @returns {void}
     *
     * @public
     */
    init: function () {
        if (!deviceDetector.isNative() && getElement(DOMElements.productMenu)[0]) {
            getElement(DOMElements.productMenu).css('display', 'block');
            subscribeToMessageBus();
        }
    },

    /**
     * Turns off the scrolling event
     *
     * @returns {void}
     *
     * @public
     */
    off: function () {

        productMenuScrollOff();
    },

};

export default api;
